import React from 'react';
import {
  IconControllerSmall,
  IconSerialnumber,
  IconTrash,
  IconAdd,
} from '@/icons';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import Input from '@/ui/Input/Input';
import CustomSelect from '@/ui/Select/Select';
import { createId } from '@paralleldrive/cuid2';
import { T } from '@/ui/Text/Text';
import { useIntl } from 'react-intl';
import { GeneralSettingsType } from '../../SettingsSidebar/types';
import { StyledGridContainer } from '../styles';

interface Props {
  controllers: GeneralSettingsType['controllers'];
  setControllers: (newValue: GeneralSettingsType['controllers']) => void;
}

function ControllerHardwareSettings({ controllers, setControllers }: Props) {
  const intl = useIntl();

  const controllerOptions = [
    { value: 'TH_LINK_4x', label: 'TH-Link 4x' },
    { value: 'TH_LINK_8x', label: 'TH-Link 8x' },
    { value: 'XN185', label: 'XN185' },
    { value: 'XM350-EM6', label: 'XM350-EM6' },
    { value: 'XM350-EM8', label: 'XM350-EM8' },
  ];

  // --- Utility Functions ---

  const updateController = (
    index: number,
    key: 'type' | 'serialnumber',
    value: string | null,
  ) => {
    const newControllers = [...controllers];
    newControllers[index] = {
      ...newControllers[index],
      [key]: value,
    };
    setControllers(newControllers);
  };

  const deleteController = (index: number) => {
    const newControllers = controllers.filter((_, i) => i !== index);
    setControllers(newControllers);
  };

  const addController = () => {
    setControllers([
      ...controllers,
      {
        id: createId(),
        type: null,
        serialnumber: null,
      },
    ]);
  };

  // --- JSX ---

  return (
    <Flex direction="column" gap={16}>
      <T h4>
        {intl.formatMessage({
          defaultMessage: 'Registered Controllers',
          id: '1MVmQ2',
        })}
      </T>
      {controllers.length !== 0 && (
        <StyledGridContainer columns={2}>
          <>
            <T body1>
              {intl.formatMessage({ defaultMessage: 'Type', id: '+U6ozc' })}
            </T>
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Serialnumber',
                id: 'B6cF7X',
              })}
            </T>
            <div />
          </>

          {controllers.map((controller, index) => {
            const hasAllValues = Boolean(
              controller.type && controller.serialnumber,
            );
            return (
              <React.Fragment key={controller.id}>
                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <CustomSelect
                    required
                    options={controllerOptions}
                    value={
                      controller.type
                        ? { value: controller.type, label: controller.type }
                        : null
                    }
                    onChange={(e) => {
                      updateController(
                        index,
                        'type',
                        e && !Array.isArray(e) ? e.value : null,
                      );
                    }}
                    size="small"
                    isSearchable={false}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Type',
                      id: '+U6ozc',
                    })}
                    icon={<IconControllerSmall />}
                  />
                </div>

                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <Input
                    required
                    size="small"
                    value={controller.serialnumber || ''}
                    onChange={(e) =>
                      updateController(index, 'serialnumber', String(e))
                    }
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Serialnumber',
                      id: 'B6cF7X',
                    })}
                    icon={<IconSerialnumber />}
                  />
                </div>

                <Button
                  size="minimum"
                  mode="transparent"
                  onClick={() => deleteController(index)}
                >
                  <IconTrash />
                </Button>
              </React.Fragment>
            );
          })}
        </StyledGridContainer>
      )}

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button mode="transparent" onClick={addController}>
          <IconAdd />
        </Button>
      </div>
    </Flex>
  );
}

export default ControllerHardwareSettings;
